import { userApi } from '@/api/account-management/userApi';
import { type User, type UserLight } from '@/models/account-management/User';
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import TransferList, { type KeyValuePair } from "../common/transfer-list/TransferList";
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { useAppContext } from '../../AppContext';

export interface UserTransferList {
    selected: UserLight[] | undefined;
    setSelected: (users: UserLight[]) => void;
    listWidth?: number;
    listHeight?: number;
}

const UserTransferList = ({ selected = [], setSelected, listWidth, listHeight }: UserTransferList) => {
    const app = useAppContext();
    const { data: userList } = useQuery<User[]>({ queryKey: ['users', app.organizationId], queryFn: () => userApi.list(app.organizationId), staleTime: Infinity });
    const [unselected, setUnselected] = useState<KeyValuePair<string>[]>([]);
    const strings = useLocalizedStrings();

    useEffect(() => {
        if (!userList) return;        
        setUnselected(userList.map(l => ({ key: l.id, value: `${l.lastName}, ${l.firstName}`})) as KeyValuePair<string>[]);
    }, [userList]);

    const getLeft = () => {
        // Remove the passed in selected items from the unselected list
        return unselected.filter(l => !selected.some(user => user.id === l.key));
    }

    const getRight = () => {
        // Map the selected ids to key value pairs
        return selected.map(s => {            
            const user = userList?.find(r => r.id === s.id);            
            return user ? { key: user.id!, value: `${user.lastName}, ${user.firstName}` } : { key: s.id, value: strings.loading };
        });
    }

    const setRight = (items: KeyValuePair<string>[]) => {
        setSelected(items.map(i => {
            const user = userList?.find(r => r.id === i.key);
            return user ? { id: user.id!, firstName: user.firstName, lastName: user.lastName, email: user.email } : { id: i.key, firstName: 'unknown', lastName: 'unknown', email: 'unknown' };
        }));
    }

    return (
        <TransferList 
            left={getLeft()} 
            setLeft={setUnselected}  
            right={getRight()}
            setRight={setRight}
            listWidth={listWidth}
            listHeight={listHeight}
        />
    );
}

export default UserTransferList;